import { UNIT_TYPES } from '@racemap/utilities/consts/units';
import type { UnitType } from '@racemap/utilities/types/units';
import { sendAnalyticsEvent } from 'src/lib/googleAnalytics';
import { MAP_STYLES, MAX_STARTER_COUNT_FLAG, TIME_MODES } from 'src/lib/player/constants';
import { t } from 'src/lib/player/i18n';
import {
  FlagContent,
  type MapType,
  PinToShadowTrackMode,
  type TimeMode,
  useStore,
} from 'src/lib/player/store';

const CDN_HOST = import.meta.env.VITE_CDN_HOST;

export default function SettingsView(): JSX.Element {
  const actions = useStore((state) => state.actions);

  const options = useStore((state) => state.options);
  const startersCount = useStore((state) =>
    state.events.reduce((r, a) => r + a.starters.length, 0),
  );
  const hasShadowTrack = useStore((state) =>
    state.events.some((event) => event.chunkedShadowTrack != null),
  );
  const onlyProjectionEvents = useStore((state) =>
    state.events.every((event) => event.modules.projection),
  );

  return (
    <form className="settings-form">
      <div>
        <span>{t('Map Type')}</span>
        <select
          value={options.mapType}
          onChange={(event) => {
            sendAnalyticsEvent('settings-map-type', `map-type-${event.target.value}`);
            actions.setMapType(event.target.value as MapType);
          }}
        >
          {MAP_STYLES.map(({ label }) => (
            <option value={label} key={label}>
              {t(`mapStyle.${label}`)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <span>{t('Unit Type')}</span>
        <select
          value={options.unitType}
          onChange={(event) => {
            sendAnalyticsEvent('settings-unit-type', `unit-type-${event.target.value}`);
            actions.setUnitType(event.target.value as UnitType);
          }}
        >
          {UNIT_TYPES.map((label) => (
            <option value={label} key={label}>
              {t(`unitType.${label}`)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <span>{t('Time Mode')}</span>
        <select
          value={options.timeMode}
          onChange={(event) => {
            sendAnalyticsEvent('settings-time-mode', `time-mode-${event.target.value}`);
            actions.setTimeMode(event.target.value as TimeMode);
          }}
        >
          {TIME_MODES.map((label) => (
            <option value={label} key={label}>
              {t(`timeMode.${label}`)}
            </option>
          ))}
        </select>
      </div>
      {startersCount <= MAX_STARTER_COUNT_FLAG ? (
        <div>
          <span>{t('Show All Flags')}</span>
          <input
            type="checkbox"
            checked={options.showAllFlags}
            onChange={(event) => {
              sendAnalyticsEvent('settings-show-flags', `show-flags-${event.target.checked}`);
              actions.setShowAllFlags(event.target.checked);
            }}
          />
        </div>
      ) : null}
      <div>
        <span>{t('Flag Content')}</span>
        <select
          value={options.overrideFlagContent ?? 'DEFAULT'}
          onChange={(event) => {
            const value = event.target.value;
            sendAnalyticsEvent('settings-flag-content', `flag-content-${value}`);
            if (value === 'DEFAULT') {
              actions.setOverrideFlagContent(null);
            } else {
              actions.setOverrideFlagContent(value as FlagContent);
            }
          }}
        >
          <option value="DEFAULT">{t('flagContent.DEFAULT')}</option>
          {Object.keys(FlagContent).map((label) => (
            <option value={label} key={label}>
              {t(`flagContent.${label}`)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <span>{t('Large Mode')}</span>
        <input
          type="checkbox"
          checked={options.largeMode}
          onChange={(event) => {
            sendAnalyticsEvent('settings-large-mode', `large-mode-${event.target.checked}`);
            actions.setLargeMode(event.target.checked);
          }}
        />
      </div>
      {hasShadowTrack && !onlyProjectionEvents ? (
        <div>
          <span>{t('Pin to track')}</span>
          <select
            value={options.pinToShadowTrack}
            onChange={(event) => {
              sendAnalyticsEvent('settings-pin-flags', `pin-flags-${event.target.value}`);
              actions.setPinToShadowTrack(event.target.value as PinToShadowTrackMode);
            }}
          >
            {Object.keys(PinToShadowTrackMode).map((label) => (
              <option value={label} key={label}>
                {t(`pinToShadowTrackMode.${label}`)}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <div>
        <a href={`${CDN_HOST}/terms-of-use`} target="_blank" rel="noreferrer noopener">
          {t('Terms of Use')}
        </a>
        &nbsp;
        <a href={`${CDN_HOST}/privacy`} target="_blank" rel="noreferrer noopener">
          {t('Privacy Policy')}
        </a>
      </div>
    </form>
  );
}
